import Option from '@creator/ui/components/Select/Option';
import Select from '@creator/ui/components/Select/Select';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import QuickCommentInput from '@src/basic-components/CommentInput/QuickCommentInput';
import CommentList from '@src/components/Post/CommentList/CommentList';
import { useStoreState } from '@src/model/hooks';
import { setCommentSortPreference } from '@src/store/comments/commentSort';
import { getEffectiveCommentSortOrder } from '@src/store/comments/helpers';
import type { CommentSortOrder } from '@src/store/contentEntities/model';
import { usePost } from '@src/store/posts/hooks/usePost';
import type { FC } from 'react';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CommentsSectionProps {
    tokenName: string;
    postId: string;
    className?: string;
    highlightCommentId?: string;
}

const CommentsSection: FC<CommentsSectionProps> = props => {
    const { className = '', postId, highlightCommentId = '', tokenName } = props;

    const { t } = useTranslation();
    const post = usePost(postId);
    const token = useStoreState(state => state.token.getToken(tokenName));

    const [sortOrder, setSortOrder] = useState<CommentSortOrder>(() => getEffectiveCommentSortOrder(tokenName, post, token));

    useEffect(() => {
        setSortOrder(getEffectiveCommentSortOrder(tokenName, post, token));
    }, [tokenName, post, token]);

    function handleSortChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const newSortOrder = event.target.value as CommentSortOrder;
        setSortOrder(newSortOrder);
        setCommentSortPreference(tokenName, newSortOrder);
    }

    function renderComments() {
        return (
            <CommentList
                includeReplies={true}
                fetchLimit={5}
                highlightCommentId={highlightCommentId}
                className=""
                tokenName={tokenName}
                type="post"
                postId={postId}
                sortOrder={sortOrder}
            />
        );
    }

    function renderTitle() {
        return <Text className="font-bold">{t('commentsSection.title')}</Text>;
    }

    function renderSortSelector() {
        return (
            <Select
                id="comments-sort-selector"
                value={sortOrder}
                onChange={handleSortChange}
                iconClassName="text-primary-500 dark:text-primary-400"
                className="space-x-1 text-sm font-medium bg-transparent border-0 h-fit text-primary-500 dark:text-primary-400 hover:bg-transparent"
            >
                <Option value="best">{t('commentsSection.sortBest')}</Option>
                <Option value="newest">{t('commentsSection.sortNewest')}</Option>
                <Option value="oldest">{t('commentsSection.sortOldest')}</Option>
            </Select>
        );
    }

    function renderCommentInput() {
        return (
            <div className="px-4 pb-3 border-b border-gray-200 dark:border-gray-800">
                <QuickCommentInput postId={postId} tokenName={tokenName} />
            </div>
        );
    }

    return (
        <div className={cn('', className)}>
            <div className="flex items-center px-4 pt-1 pb-3 space-x-2 text-sm">
                {renderTitle()}
                {renderSortSelector()}
            </div>
            {renderCommentInput()}
            {renderComments()}
        </div>
    );
};

export default memo(CommentsSection);
